// src/pages/LCDesk.js
//
// THE LOCAL CHAIRPERSON'S DESK (weekend plan, batch 2).
//
// A DESK, NOT A TABLE. The Claims page answers "what is in my territory" and answers it well. It
// does not answer the question an officer actually opens the platform with, which is "what needs me
// today, and in what order". Ordering by filed date - which is what Claims does - puts a claim with
// four days of filing window left underneath one filed yesterday with sixty.
//
// THE TWO UNAVAILABLE SECTIONS ARE NOW BUILT - CORRECTED 2026-08-29, AND THE HISTORY IS KEPT
// BECAUSE IT IS THE POINT.
//
// This page shipped in batch 2 with two sections rendered as NOT BUILT YET, each carrying a careful
// explanation of why the platform could not honestly fill it. Both explanations were true when
// written. BOTH WERE FALSE WITHIN THIRTY-SIX HOURS, and the page went on stating them:
//
//   "sent up, and where it went"   said there is no gc_review stage. BATCH 4 ADDED ONE the next day.
//   "returned to you"              said "there is no return to draft, and there will not be one",
//                                  citing 61.6. RULING 72 AMENDED 61.6 THE SAME DAY and restored the
//                                  return - src\data\claimReturn.js, 63 assertions, enforced in
//                                  firestore.rules by windowIntact().
//
// So a Local Chairperson opening his own desk was told a feature he had would never exist. The
// assistant built ruling 72's machinery and did not come back to the page written to explain its
// absence. Found by the register re-total of 2026-08-29 (data\YCP_REGISTER_RETOTAL_2026-08-29.md
// SS3), and the general lesson is recorded there: WHEN A RULING REVERSES A RULING, THE REVERSAL HAS
// TO SWEEP THE SURFACES BUILT TO EXPLAIN THE OLD ONE. A page that documents why something is missing
// becomes a page that lies the moment the thing arrives, and the care in the explanation is exactly
// what makes it convincing.
//
// WHAT IS STILL HONESTLY UNAVAILABLE, AND IS SAID RATHER THAN QUIETLY DROPPED: batch 4 built the
// STAGE, not an elevation EVENT. There is no `elevatedBy` and no `elevatedAt`, and a GC can set
// gc_review too - so "sent up" names where a claim IS and never claims to record who moved it. That
// distinction is kept on the face of the section (PD-4, PD-11).
//
// EVERYTHING HERE IS COMPUTED FROM DATA THAT ALREADY EXISTS. No new claim field, no schema change,
// no rules change. Same query as Claims.js, same guards for the two silent-empty failures that page
// already defends against - an unprovisioned LC and a scope list past Firestore's 30-value cap.

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { subscribeVisibleClaims } from '../data/claimQueries';
import ClaimTally from '../components/ClaimTally';   // section 493: the read-only claim tally
import { partitionDesk, WINDOW_URGENT_DAYS } from '../data/deskQueue';
import DeskClocksPanel from '../components/DeskClocksPanel';   // master list item 6 (Bob 2026-10-14)
import { withdrawnRows, deskClockRows, boardRows } from '../data/deskClocks';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { returnRowsForDesk } from '../data/claimReturn';   // section 488: ruling 72's queue entry, persisted
import FrozenVersion from '../components/FrozenVersion';   // ruling 72.2 / 72.3 item 2 (Record 583): the officer keeps the frozen copy
import AssignmentsPanel from '../components/AssignmentsPanel';   // G8.2 - ruling 17
import { ReviewsOfMyOffice } from '../components/ReviewPanel';    // G8.2 - ruling 69 transparency
import RulingRef from '../components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import WarRoomInvitations from '../components/WarRoomInvitations';   // 6a-04 - ruling 17, only invitees may be present
import WarRoomActionsDesk from '../components/WarRoomActionsDesk';   // section 532
import SeatsPanel from '../components/SeatsPanel';   // WO-001 S5/S7 (section 252): a Local Chairperson closes his own term, takes up a vacant seat as its designated vice, hands his seat over - the panel gates every act by the rules
import PresentmentDirectoryPanel from '../components/PresentmentDirectoryPanel';   // Record 610 (ruling 262/259): the LC's directory and his seat's office address
import SignoffPanel from '../components/SignoffPanel';   // ruling 230 - the officer's own signature sign-off
import S44DeskPanel from '../components/S44DeskPanel';   // Record 593 (ruling 205 audit): served §44 notices not yet acknowledged
import useSeatsOfRecord from '../hooks/useSeatsOfRecord';   // WO-001 S3 - the roster is the spine's projection
import { capScopes } from '../data/scopeCap';   // T5 item 20: the 30-territory cap, in one place
import MemberStandingFlags from '../components/MemberStandingFlags';   // Bob 2026-10-25: the S&T's private standing marks reach the LC
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

// THE ORDERING LIVES IN src/data/deskQueue.js, NOT HERE, and it is proved by
// scripts/test-lc-desk.mjs. A queue whose order is only ever checked by looking at a screen is a
// queue nobody has checked. This file renders what that module returns and adds nothing to it.

const C = {
  panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF',
  accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2',
};

// RULING 276, SCREEN 4 (2026-10-16): the desk's one job is today's work for the seat. A block with nothing in it (empty)
// shows one line - its title and its count - and its explanation behind "show"; nothing is removed, and a block with
// anything in it opens as before. The training video's words ("the blocks, each with a count on the right") stay true.
function Block({ title, subtitle, tone, count, children, empty }) {
  const colour = tone === 'unavailable' ? C.dim : tone === 'bad' ? C.bad : tone === 'warn' ? C.accent : C.ok;
  const [shown, setShown] = React.useState(false);
  if (empty && !shown) {
    return (
      <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 6, display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', padding: '6px 14px', gap: 12 }}>
        <div style={{ fontSize: 12, color: C.dim }}>{title}</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.ok, whiteSpace: 'nowrap' }}>{count}</span>
          <button type="button" className="btn btn-secondary" style={{ fontSize: 10, padding: '1px 8px' }} aria-expanded="false" onClick={() => setShown(true)}>show</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '10px 14px', borderBottom: '1px solid ' + C.line, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{subtitle}</div> : null}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: colour, whiteSpace: 'nowrap' }}>
          {tone === 'unavailable' ? 'NOT BUILT YET' : count}
        </div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function ClaimRow({ x, onOpen, note }) {
  const c = x.claim;
  const window = x.left === null
    ? <span style={{ color: C.dim }}>no filing window on record</span>
    : x.left < 0
      ? <span style={{ color: C.bad }}>window passed {Math.abs(x.left)}d ago</span>
      : <span style={{ color: x.left <= WINDOW_URGENT_DAYS ? C.accent : C.dim }}>{x.left}d of window left</span>;
  return (
    <div {...activate(() => onOpen && onOpen(c.docId || c.id))}
         style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0',
                  borderBottom: '1px dotted ' + C.line, cursor: onOpen ? 'pointer' : 'default' }}>
      <div>
        <span style={{ color: C.text }}>{c.id || c.docId || '(no claim number)'}</span>
        <span style={{ color: C.dim }}>{c.claimantName ? '  -  ' + c.claimantName : ''}</span>
        <div style={{ fontSize: 11, color: C.dim }}>
          {String(c.status || 'unknown status')}
          {x.age !== null ? '  -  ' + x.age + 'd on the platform' : '  -  age unknown'}
          {note ? '  -  ' + note : ''}
        </div>
      </div>
      <div style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{window}</div>
    </div>
  );
}

export default function LCDesk({ user, onViewClaim, onNavigate, onOpenMember }) {
  const [claims, setClaims] = useState([]);
  const [frozenOpen, setFrozenOpen] = useState(null);   // Record 583: which returned claim's frozen copy is open
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // The same two silent-empty failures Claims.js defends against. They are repeated rather than
  // shared because this page must never render "nothing needs you today" when the truth is that the
  // account cannot see its own territory.
  const lcNotProvisioned = user?.role === 'local_chair'
    && !(Array.isArray(user?.scopes) && user.scopes.length > 0);
  const scopesTruncated = user?.role === 'local_chair'
    && Array.isArray(user?.scopes) && user.scopes.length > 30;

  useEffect(() => {
    // F-15 (SS201): shaped in src\data\claimQueries.js - `status in <non-draft>` beside the scope,
    // one query per territory - because the read rule excludes drafts and an unshaped query is
    // refused outright. This desk was refused for every Local Chairperson from 2026-08-29 to today.
    const unsub = subscribeVisibleClaims(db, user, rows => {
      setClaims(rows);
      setLoadError('');
      setLoading(false);
    }, err => {
      console.error('[LCDesk] query failed:', err);
      setLoadError(err && err.code ? err.code + ': ' + err.message : ((err && err.message) || 'Failed to load claims'));
      setLoading(false);
    });
    return unsub;
  }, [user?.uid, user?.role, user?.committee, user?.isAdmin, (user?.scopes || []).join('|')]);

  // Section 488: the claims he sent back and can no longer read. One query per territory, the shape the
  // rules can prove (as claimQueries.js does). A failure here is shown, never treated as "none returned".
  const [returns, setReturns] = useState([]);
  const [returnsError, setReturnsError] = useState('');
  useEffect(() => {
    const scopes = (user && user.role === 'local_chair' && Array.isArray(user.scopes)) ? capScopes(user).scopes : [];
    if (!scopes.length) { setReturns([]); return undefined; }
    const byScope = {};
    const unsubs = scopes.map(s => onSnapshot(query(collection(db, 'claimReturns'), where('scopeKey', '==', s)), snap => {
      byScope[s] = snap.docs.map(x => x.data());
      setReturns(Object.values(byScope).flat());
      setReturnsError('');
    }, err => { setReturnsError((err && err.code) || 'the returned-claims record could not be read'); }));
    return () => unsubs.forEach(u => u());
  }, [user?.uid, user?.role, (user?.scopes || []).join('|')]);

  // Onboarding one-click (Bob 2026-10-26): applications to join his territory land here as approve/decline.
  const [pendingApps, setPendingApps] = useState(0);
  useEffect(() => {
    const scopes = (user && user.role === 'local_chair' && Array.isArray(user.scopes)) ? capScopes(user).scopes : [];
    if (!scopes.length) { setPendingApps(0); return undefined; }
    const byScope = {};
    const unsubs = scopes.map(s => onSnapshot(query(collection(db, 'applications'), where('scopeKey', '==', s), where('status', '==', 'pending')), snap => {
      byScope[s] = snap.size; setPendingApps(Object.values(byScope).reduce((a, b) => a + b, 0));
    }, () => {}));
    return () => unsubs.forEach(u => u());
  }, [user?.uid, (user?.scopes || []).join('|')]);

  const seats = useSeatsOfRecord(user && user.committee);   // S3: desk ordering reads the spine, the bundle only until seeded
  const deskClaims = useMemo(() => claims.concat(returnRowsForDesk(returns, claims.map(c => c.docId))), [claims, returns]);
  const d = useMemo(() => partitionDesk(deskClaims, { roster: seats.roster }), [deskClaims, seats.roster]);
  // Master list item 6: the clocks, the Board stage and the withdrawn claims, from the claims he can read (deskClocks.js).
  const item6 = useMemo(() => ({ clocks: deskClockRows(claims), board: boardRows(claims), withdrawn: withdrawnRows(claims) }), [claims]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.dim }}>Loading your desk...</div>;
  }

  if (lcNotProvisioned) {
    return (
      <div style={{ maxWidth: 760, margin: '32px auto', padding: 24, border: '1px solid ' + C.accent, background: C.panel }}>
        <div style={{ color: C.accent, fontWeight: 700, marginBottom: 8 }}>This account is not provisioned for a territory</div>
        <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>
          Your account carries the Local Chairperson role but no territory, so no member's claim can reach it.
        </div>
        <div style={{ color: C.dim, fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
          <b>This is not a report that your desk is clear.</b> It is a report that the platform does not yet
          know which territory is yours. Your General Chairperson assigns the seat.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ maxWidth: 760, margin: '32px auto', padding: 24, border: '1px solid ' + C.bad, background: C.panel }}>
        <div style={{ color: C.bad, fontWeight: 700, marginBottom: 8 }}>Couldn't load your desk</div>
        <div style={{ color: '#F4A0A0', fontSize: 12, fontFamily: 'monospace' }}>{loadError}</div>
        <div style={{ color: C.dim, fontSize: 12, marginTop: 10 }}>
          This is a load failure, not an empty desk.
        </div>
      </div>
    );
  }

  const open = (id) => onViewClaim && onViewClaim(id);

  // NEEDS YOU NOW (2026-10-26 first-screen redesign, Bob): lead with what is actually waiting on him.
  const needs = [
    d.expired.length && { tone: C.bad, label: d.expired.length + ' claim(s) - filing window already passed' },
    d.closing.length && { tone: C.accent, label: d.closing.length + ' claim(s) - filing window closing within ' + WINDOW_URGENT_DAYS + ' days' },
    d.awaiting.length && { tone: C.accent, label: d.awaiting.length + ' claim(s) awaiting your review' },
    d.returned.length && { tone: C.accent, label: d.returned.length + ' claim(s) sent back - still on your clock' },
    d.unrouted.length && { tone: C.accent, label: d.unrouted.length + ' claim(s) unrouted in your territory' },
    d.unheld.length && { tone: C.accent, label: d.unheld.length + ' claim(s) routed to a seat nobody holds' },
    pendingApps > 0 && { tone: C.accent, label: pendingApps + ' application(s) to join your territory - approve or decline', nav: 'applications' },
  ].filter(Boolean);

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px 28px' }}>
      {/* WHO YOU ARE */}
      <div style={{ padding: '18px 0 2px' }}>
        <h1 style={{ margin: 0, color: C.text, fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>
          {user?.name || 'Local Chairperson'}
        </h1>
        <div style={{ color: C.dim, fontSize: 13, marginTop: 3 }}>
          {user?.name ? 'Local Chairperson · ' : ''}{user?.committee || 'your committee'} · {claims.length} claim(s) in your territory
        </div>
      </div>

      {/* NEEDS YOU NOW */}
      <div style={{ margin: '18px 0 6px', color: C.dim, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}>Needs you now</div>
      {needs.length === 0
        ? <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid ' + C.line, color: C.ok, fontSize: 14 }}>
            You're all clear - nothing is waiting on you right now.
          </div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {needs.map((n, i) => (
              n.nav
                ? <button key={i} type="button" onClick={() => onNavigate && onNavigate(n.nav)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid ' + n.tone, background: C.panel, cursor: 'pointer', width: '100%', textAlign: 'left', font: 'inherit' }}>
                    <span style={{ flex: 1, color: n.tone, fontSize: 14, fontWeight: 600 }}>{n.label}</span>
                    <span style={{ color: n.tone, fontSize: 12 }}>open &rarr;</span>
                  </button>
                : <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid ' + n.tone, background: C.panel }}>
                    <span style={{ flex: 1, color: n.tone, fontSize: 14, fontWeight: 600 }}>{n.label}</span>
                  </div>
            ))}
          </div>}

      {/* YOUR WORK */}
      <div style={{ margin: '24px 0 6px', color: C.dim, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}>Your work</div>

      {scopesTruncated && (
        <div style={{ border: '1px solid ' + C.accent, background: '#2D1A00', color: C.accent,
                      padding: '10px 14px', margin: '10px 0 16px', fontSize: 12 }}>
          This account holds {user.scopes.length} territories and this page can query only 30 of them.
          Claims in the other {user.scopes.length - 30} are <b>not</b> below, and that is not an empty result.
        </div>
      )}

      <AssignmentsPanel user={user} mode="assignee" onOpenClaim={open} />
      <ReviewsOfMyOffice user={user} />
      <WarRoomInvitations user={user} />
      <WarRoomActionsDesk user={user} />   {/* section 532: action items from War Room occasions, until done */}

      <details style={{ marginBottom: 14 }}>
        <summary style={{ cursor: 'pointer', color: C.text, fontSize: 14, fontWeight: 600, padding: '10px 0' }}>Local tools</summary>
        <div style={{ marginTop: 8 }}>
          <ClaimTally user={user} mode="matrix" onOpenMember={onOpenMember} />
          <S44DeskPanel claims={claims} user={user} onOpen={open} />
          <SignoffPanel user={user} />
          <PresentmentDirectoryPanel user={user} mode="lc" />
          <SeatsPanel user={user} committee={user && user.committee} />
        </div>
      </details>

      <MemberStandingFlags user={user} />

      <Block title="Filing window already passed" tone="bad" count={d.expired.length + ' claim(s)'} empty={d.expired.length === 0}
             subtitle="Counted from expiresAt, which is written only where the agreement is catalogued and a date of occurrence exists">
        {d.expired.length === 0
          ? 'No claim in your territory has a filing window that has run out.'
          : d.expired.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} />)}
      </Block>

      <Block title={'Filing window closing within ' + WINDOW_URGENT_DAYS + ' days'} tone="warn" count={d.closing.length + ' claim(s)'} empty={d.closing.length === 0}
             subtitle={'Soonest first. Ten days is the assistant\'s threshold, not a ruling  -  one constant, changed in one line'}>
        {d.closing.length === 0
          ? 'Nothing is inside the ' + WINDOW_URGENT_DAYS + '-day mark.'
          : d.closing.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} />)}
      </Block>

      <Block title="Awaiting your review" tone={d.awaiting.length ? 'warn' : 'ok'} count={d.awaiting.length + ' claim(s)'} empty={d.awaiting.length === 0}
             subtitle="Submitted or in intake - sitting on your desk. Soonest deadline first; a claim with no computable deadline sorts last rather than looking safe">
        {d.awaiting.length === 0
          ? 'Nothing is waiting on you.'
          : d.awaiting.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} />)}
      </Block>

      <Block title="Sent up, and where it went" empty={d.elevated.length === 0 && d.pastDesk.length === 0}
             tone={d.elevated.length ? 'warn' : 'ok'}
             count={d.elevated.length + ' at GC Review'}
             subtitle="Soonest deadline first. A claim at GC Review is still burning its filing window">
        {d.elevated.length === 0
          ? 'Nothing of yours is sitting at GC Review.'
          : d.elevated.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} note="awaiting your General Chairperson" />)}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dotted ' + C.line, fontSize: 11 }}>
          <b style={{ color: C.text }}>This is where the claim IS, not a record of who moved it.</b> The
          platform stores no elevation event - no <code>elevatedBy</code>, no <code>elevatedAt</code> - and a
          General Chairperson can set <code>gc_review</code> too. So this section names the stage truthfully
          and stops there rather than telling you that you sent something up.
          {d.pastDesk.length > 0 && (
            <div style={{ marginTop: 6 }}>
              A further <b style={{ color: C.text }}>{d.pastDesk.length} claim(s)</b> sit past GC Review
              entirely - valuation onward.
            </div>
          )}
        </div>
      </Block>

      <Block title="Sent back to its author, and still on your clock" empty={d.returned.length === 0 && !returnsError}
             tone={d.returned.length ? 'warn' : 'ok'}
             count={d.returned.length + ' with the member'}
             subtitle={<>Oldest return first - a claim on its third week back is the one nobody is holding<RulingRef user={user} n={72} why="A claim sent back to its author stays on the officer's desk with its clock running." /></>}>
        {returnsError ? <div style={{ color: C.bad, fontSize: 12, marginBottom: 6 }}>The record of claims you sent back could not be read ({returnsError}). This list may be incomplete.</div> : null}
        {d.returned.length === 0
          ? 'You have not sent any claim back to its author.'
          : d.returned.map(x => (
              <React.Fragment key={x.claim.docId}>
                <ClaimRow x={x} onOpen={x.claim.returnOnly ? null : open}
                          note={'returned ' + String(x.claim.returnedAt || '').slice(0, 10)
                                + (Number(x.claim.returnCount) > 1 ? '  -  trip ' + x.claim.returnCount : '')} />
                {frozenOpen === x.claim.docId
                  ? <FrozenVersion claimId={x.claim.docId} returnCount={x.claim.returnCount} onClose={() => setFrozenOpen(null)} />
                  : <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 10px', margin: '4px 0 8px' }}
                            onClick={() => setFrozenOpen(x.claim.docId)}>Frozen copy (SUPERSEDED)</button>}
              </React.Fragment>
            ))}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dotted ' + C.line, fontSize: 11 }}>
          <b style={{ color: C.text }}>The return does not pause the clock and it never will.</b><RulingRef user={user} n={72} why="Sending a claim back to its author does not pause or reset the member's filing window." /> The
          claim sits with its author, where you cannot see or work it - but the filing window
          keeps running, so it stays on this desk with its deadline counting. What you see here is the record
          of YOUR act: the number, when you sent it, your own reason, and how many trips it has made. None of
          the member's content appears, and that is what makes the return lawful.<RulingRef user={user} n={56} why="A draft belongs to its author alone. While the claim is back with the member, no officer can open the member's working copy." />
        </div>
      </Block>

      <Block title="Unrouted in your territory" tone={d.unrouted.length ? 'warn' : 'ok'} count={d.unrouted.length + ' claim(s)'} empty={d.unrouted.length === 0}
             subtitle="The work location resolved to no seat at all. The remedy is to map a location - your General Chairperson can">
        {d.unrouted.length === 0
          ? 'Nothing unrouted.'
          : d.unrouted.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} />)}
      </Block>

      <Block title="Routed to a seat nobody holds" tone={d.unheld.length ? 'warn' : 'ok'} count={d.unheld.length + ' claim(s)'} empty={d.unheld.length === 0}
             subtitle={<>Resolved perfectly, to a VACANT seat. The work and the authority vest in the General Chairperson<RulingRef user={user} n={60} why="When a Local Chairperson's seat is vacant, the work and authority over its claims and cases belong to the General Chairperson." /></>}>
        {d.unheld.length === 0
          ? 'Nothing is pointed at a vacant seat.'
          : d.unheld.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} />)}
      </Block>

      <DeskClocksPanel clocks={item6.clocks} board={item6.board} withdrawn={item6.withdrawn} onOpen={open} />

      <Block title="No filing window on record" tone={d.noClock.length ? 'warn' : 'ok'} count={d.noClock.length + ' claim(s)'} empty={d.noClock.length === 0}
             subtitle="No deadline is computed and none is shown - the member is told nothing about the clock he is on">
        {d.noClock.length === 0
          ? 'Every claim in your territory carries a computable filing window.'
          : <>
              <div style={{ marginBottom: 6 }}>
                <code>expiresAt</code> is written only where the agreement has a filing-window profile and a
                date of occurrence exists. Where it is absent this page shows nothing rather than guessing,
                and lists the claim here so the absence is visible rather than blank.
              </div>
              {d.noClock.map(x => <ClaimRow key={x.claim.docId} x={x} onOpen={open} />)}
            </>}
      </Block>

      <div style={{ color: C.dim, fontSize: 11, lineHeight: 1.6, borderTop: '1px solid ' + C.line, paddingTop: 10 }}>
        Built entirely from claim data already stored - no new field, no schema change, no rules change.
        Ages come from <code>createdAt</code> via <code>claimFiledAtSeconds()</code>; deadlines from{' '}
        <code>expiresAt</code>, written by <code>computeExpiresAt()</code>. An unknown age prints as
        unknown and an absent deadline prints as absent. Nothing above is faked from a status that means
        something else: where the platform records only a stage and not an act, the section says so.
        {onNavigate ? <> The full territory list is on the{' '}
          <span {...activate(() => onNavigate('claims'))} style={{ color: C.accent, cursor: 'pointer' }}>Claims</span> page.</> : null}
      </div>
    </div>
  );
}
